import React, { useMemo } from 'react';
import { FileError } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { AppButton, Box, Preloader, Text } from '@common-fe/common-fe';

import { FileImage } from '@/common/constants';
import { Modal } from '@/components';
import ScanPreviewContainer from '@/components/controls/DropScan/ScanPreview/ScanPreviewContainer';
import useFilesInDocumentStore from '@/components/controls/DropScan/useFilesInDocument.store';

interface Props {
  storeKey: string;
  title?: string;
  onSetVisible: (value: boolean) => void;
  onScan: () => void;
  onBack: () => void;
  onSetFiles: (files: FileImage[]) => void;
  onDeleteFile: (fileId: string) => void;
  onFileUploadError: (error?: FileError) => void; 
  isLoading?: boolean;
  isMobile?: boolean;
}

const PreviewFilePopup: React.FC<Props> = ({
  storeKey,
  title,
  onSetVisible,
  onScan,
  onBack,
  onSetFiles,
  onDeleteFile,
  onFileUploadError,
  isLoading,
  isMobile,
}) => {
  const { t } = useTranslation();
  const { values: { files }} = useFilesInDocumentStore(storeKey);

  const allFiles = useMemo(() => (files?.length ? files : []), [files]);

  return (
    <Modal
      visible
      onSetVisible={onSetVisible}
      testId="preview-file"
    >
      <Box>
        {title && (
          <Text
            data-testid="preview-file-modal_title"
            size="2xl"
            weight={700}
            textAlign="center"
            margin={{ bottom: 'spacing24' }}
          >
            {title}
          </Text>
        )}

        <Box
          pad="spacing12"
          round="container1Round"
          background="module"
          {...isMobile ? {} : { flex: 'grow' }}
        >
          <ScanPreviewContainer
            isShowTooltip={false}
            isActiveFileShown
            storeKey={storeKey}
            defaultFiles={allFiles}
            handleDeleteFile={onDeleteFile}
            onSetFiles={onSetFiles}
            onFileUploadError={onFileUploadError}
            ocrRequired
          />
        </Box>

        <Box
          data-testId="preview-file-modal_footer"
          margin={{ top: 'spacing24' }}
          direction="row"
          gap="spacing12"
          justify="end"
        >
          <Box width={{ max: isMobile ? '50%' : '160px', width: '100%'}}>
            <AppButton
              testId="preview-file-modal-back"
              width="100%"
              buttonType="secondary"
              onClick={onBack}
            >
              {t('Close')}
            </AppButton>
          </Box>
          <Box width={{ max: isMobile ? '50%' : '160px', width: '100%'}}>
            <AppButton
              testId="preview-file-modal-confirm"
              width="100%"
              onClick={onScan}
              disabled={isLoading || !allFiles?.length}
            >
              {isLoading
                ? <Preloader color="white" testId="preview-file-confirm_loader" />
                : t('Confirm')}
            </AppButton>
          </Box>
        </Box> 
      </Box>
    </Modal>
  );
};

export default PreviewFilePopup;
